import Card from '../../components/card/Card';
import { useNavigate, useParams } from 'react-router-dom';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  activateClientLines,
  getInstrumentationId,
  getInstrumentationStatus,
} from '../../services/InstrumentationService';
import { amount, clientData, Line, InstrumentationProgress, InstrumentationStatus } from '../../types/interfaces';
import LineStatusIndicator from './LineStatusIndicator';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import { economicActivities } from '../../mocks/EconomicActivities';
import { formatCurrency } from '../../utils/Masks';
import { hasData, isSuccessfulResponse } from '../../utils/Utils';
import Image from '../../components/image/Image';
import { getProductLines } from '../../services/ClientService';
import RejectApplicationModal from '../common/Modals/RejectApplicationModal';
import { APPLICATION_STATUS } from '../../utils/Constants';
import { Toast, useToast } from '../../components/toast/toast';
import { updateApplication } from '../../services/ApplicationService';
import { routes } from '../../routes/routePaths';
import CloseApplicationModal from './InstrumentationModals/CloseApplicationModal';
import Spinner from '../../components/Spinner/spinner';
import H from '../../components/texts/h/H';

interface ClientProps {
  cuit: string | undefined;
  clientData: clientData;
  onPreviousClick: () => void;
  handleRetryGet: () => void;
  lines: Line[];
  applicationId: string | undefined;
}

const ClientLines: React.FC<ClientProps> = ({
  clientData,
  lines,
  onPreviousClick,
  applicationId,
}) => {
  const { cuit, id } = useParams();
  const navigate = useNavigate();

  const [isAllActivated, setIsAllActivated] = useState<boolean>(false);
  const [isOneClickEnabled, setIsOneClickEnabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isActivatingLines, setIsActivatingLines] = useState<boolean>(false);
  const [instrumentationId, setInstrumentationId] = useState(undefined);
  const [hasAnyNullInstrumentation, setHasAnyNullInstrumentation] =
    useState<boolean>(false);
  const [productLines, setProductLines] = useState<any>();
  const [isRejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const [isCloseModalOpen, setCloseModalOpen] = useState<boolean>(false);
  const isContractLineActivated =
    instrumentationId !== undefined || instrumentationId !== null;
  const [hasActivated, setHasActivated] = useState<boolean>(false);
  const [isClosing, setClosing] = useState<boolean>(false);
  const [instrumentationStatus, setInstrumentationStatus] = useState<Map<number, InstrumentationProgress>>(new Map());
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isGettingInstrumentationIdRef = useRef<boolean>(false);

  const getErrorMessage = (errorCode?: string, defaultMessage?: string): string => {
    // Mensajes específicos según código de error
    switch (errorCode) {
      case 'LIMIT_DUPLICATE':
        return 'El límite por línea ya existe. No se puede duplicar.';
      case 'LIMIT_NOT_FOUND':
        return 'No existe un límite por línea general instrumentado. Se requiere crear el límite antes de continuar.';
      case 'ONECLICK_500':
        return 'Error temporal al comunicarse con OneClick. Por favor, intente nuevamente.';
      case 'ONECLICK_NOT_FOUND':
        return 'No se encontró el recurso solicitado en OneClick.';
      case 'ONECLICK_EMPTY_RESPONSE':
        return 'OneClick retornó una respuesta vacía. Por favor, intente nuevamente.';
      default:
        return defaultMessage || 'Ocurrió un error al activar las líneas de producto';
    }
  };

  const handleGetInstrumentationId = useCallback(() => {
    // Nota: El guard se establece en el useEffect antes de llamar esta función
    // Aquí solo ejecutamos la lógica, sin verificar el guard nuevamente
    setIsLoading(true);
    
    getInstrumentationId(cuit, id)
      .then((response) => {
        // Verificar si la respuesta es exitosa
        if (response.success && hasData(response) && isSuccessfulResponse(response)) {
          setInstrumentationId(response.data.instrumentation_id);
        } else {
          // Manejar respuesta con error
          const errorResponse = response as { success: false; message?: string; code?: string; status?: number };
          const errorCode = errorResponse.code;
          const errorMessage = getErrorMessage(errorCode, errorResponse.message || 'Error desconocido al configurar garantías');
          
          useToast.error({
            title: errorMessage,
            id: 'instrumentation-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.error('Error getting instrumentation ID:', error);
        
        // Intentar extraer código y mensaje del error
        const errorCode = error.code || error.response?.data?.error || error.response?.data?.code;
        const errorMessage = error.message || error.response?.data?.message;
        const userMessage = getErrorMessage(errorCode, errorMessage || 'Error desconocido al configurar garantías');
        
        useToast.error({
          title: userMessage,
          id: 'instrumentation-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      })
      .finally(() => {
        setIsLoading(false);
        isGettingInstrumentationIdRef.current = false;
      });
  }, [cuit, id]);

  // useEffect 1: Cargar productLines solo una vez
  useEffect(() => {
    if (!productLines) {
      getProductLines().then((response) => {
        if (hasData(response) && isSuccessfulResponse(response)) {
          setProductLines(response.data);
        }
      });
    }
  }, [productLines]);

  // useEffect 2: Actualizar estados derivados cuando cambian approved_products
  useEffect(() => {
    if (!clientData.approved_products) return;
    
    const hasNullInstrumentation = (clientData.approved_products as Line[]).some(
      (line: Line) => line.instrumentation_id === null
    );
    setHasAnyNullInstrumentation(hasNullInstrumentation);

    const hasAllActivated = (clientData.approved_products as Line[]).every(
      (line: Line) => line.instrumentation_id !== null
    );
    setIsAllActivated(hasAllActivated);
  }, [clientData.approved_products]);

  // useEffect 3: Llamar handleGetInstrumentationId solo cuando es necesario
  useEffect(() => {
    // Verificar si instrumentation_id es null, undefined o string vacío
    const hasInstrumentationId = clientData.instrumentation_id !== null && 
                                  clientData.instrumentation_id !== undefined && 
                                  clientData.instrumentation_id !== '';
    
    if (!hasInstrumentationId && !isGettingInstrumentationIdRef.current) {
      // Establecer el guard ANTES de llamar para hacer el check y set más atómicos
      isGettingInstrumentationIdRef.current = true;
      handleGetInstrumentationId();
    } else if (hasInstrumentationId) {
      setIsLoading(false);
    }
  }, [clientData.instrumentation_id, handleGetInstrumentationId]);

  // Polling effect
  useEffect(() => {
    const hasInProgress = Array.from(instrumentationStatus.values()).some(
      progress => progress?.status === InstrumentationStatus.IN_PROGRESS
    );

    if (hasInProgress && !isPolling) {
      startPolling();
    } else if (!hasInProgress && isPolling) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [instrumentationStatus, isPolling]);

  const startPolling = () => {
    if (isPolling) return; // Evitar múltiples intervalos
    
    setIsPolling(true);
    pollingIntervalRef.current = setInterval(() => {
      fetchInstrumentationStatus();
    }, 2000); // Poll cada 2 segundos
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  const fetchInstrumentationStatus = async () => {
    try {
      const response = await getInstrumentationStatus(id);
      if (hasData(response) && isSuccessfulResponse(response)) {
        const statusMap = new Map<number, InstrumentationProgress>();
        response.data.product_lines.forEach((line: any) => {
          if (line.progress) {
            statusMap.set(line.id, line.progress);
          }
        });
        setInstrumentationStatus(statusMap);
        
        // Detener polling si todas las líneas terminaron
        const allFinished = response.data.product_lines.every((line: any) => 
          line.status === InstrumentationStatus.SUCCESS || 
          line.status === InstrumentationStatus.FAILED
        );
        
        if (allFinished) {
          stopPolling();
        }
      }
    } catch (error) {
      console.error('Error fetching instrumentation status:', error);
      stopPolling();
    }
  };

  const handleActivateLines = () => {
    setIsActivatingLines(true);
    setHasActivated(true);
    
    activateClientLines(cuit, id)
      .then((response) => {
        // Verificar tanto success como status
        if (response.success && hasData(response) && isSuccessfulResponse(response)) {
          // Actualizar estado local con la respuesta
          const statusMap = new Map<number, InstrumentationProgress>();
          response.data.approved_product_lines.forEach((line: Line) => {
            if (line.instrumentation_progress) {
              statusMap.set(line.id, line.instrumentation_progress);
            }
          });
          setInstrumentationStatus(statusMap);
          
          // Check if OneClick is enabled
          const oneClickEnabled = response.data.oneclick_enabled !== false;
          setIsOneClickEnabled(oneClickEnabled);
          
          // If OneClick is disabled, allow closing the folder immediately
          if (!oneClickEnabled) {
            setIsAllActivated(true);
            setHasAnyNullInstrumentation(false);
          } else {
            const hasNullInstrumentation =
              response.data.approved_product_lines.some(
                (line: Line) => line.instrumentation_id === null
              );
            setHasAnyNullInstrumentation(hasNullInstrumentation);
            const hasAllActivated = response.data.approved_product_lines.every(
              (line: Line) => line.instrumentation_id !== null
            );
            setIsAllActivated(hasAllActivated);
          }
        } else {
          // Manejar error con código específico
          const errorResponse = response as { success: false; message?: string; code?: string; status?: number };
          const errorCode = errorResponse.code;
          const errorMessage = getErrorMessage(errorCode, errorResponse.message);
          
          useToast.error({
            title: 'Error al activar líneas',
            id: 'instrumentation-toast',
            description: errorMessage,
            closeButton: true,
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.error('Error activating lines:', error);
        
        // Intentar extraer código y mensaje del error
        const errorCode = error.code || error.response?.data?.error || error.response?.data?.code;
        const errorMessage = error.message || error.response?.data?.message;
        const userMessage = getErrorMessage(errorCode, errorMessage);
        
        useToast.error({
          title: 'Error al activar líneas',
          id: 'instrumentation-toast',
          description: userMessage,
          closeButton: true,
          autoClose: 5000,
        });
        
        // Forzar una actualización del estado para reflejar el error si es necesario
        fetchInstrumentationStatus();
      })
      .finally(() => {
        setIsActivatingLines(false);
      });
  };

  const InfoRow = ({
    label,
    value,
    fullWidth = false,
  }: {
    label: string;
    value: string | undefined;
    fullWidth?: boolean;
  }) => (
    <Card display='flex' width={fullWidth ? '100%' : '48%'} gap='8px'>
      <P type='2' weight='bold' variant='neutral'>
        {label}
      </P>
      <P type='2' variant='neutral'>
        {value}
      </P>
    </Card>
  );

  const ClientDataBox = () => {
    const { basic_data, cuit, referrer_name, officer } = clientData;
    const actividad = economicActivities.find(
      (e) => e.code === Number(basic_data.activity)
    )?.label;

    const ivaStatus = ivaStatuses.find(
      (i) => i.code === basic_data.iva_status
    )?.label;

    return (
      <Card>
        {(clientData.id_application === null || !isContractLineActivated) && (
          <Card
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            variant='warning'
            height='40px'
          >
            <Card display='flex'>
              <Image name='warning_line' variant='warning' modifier='strong' />
              <Card margin={{ left: '3' }}>
                <P type='2' variant='neutral' weight='bold'>
                  Hubo un error de comunicación con One Click: Volvé a
                  intentarlo.
                </P>
              </Card>
            </Card>
            <Image
              name='retry_data'
              variant='warning'
              modifier='strong'
              cursor='pointer'
              onClick={() => handleGetInstrumentationId()}
            />
          </Card>
        )}
        <P variant='neutral' type='1' weight='bold' modifier='strong'>
          Datos del cliente
        </P>
        <Card
          display='flex'
          flexWrap='wrap'
          spacing='3'
          variant='neutral'
          modifier='regular'
          margin={{ bottom: '4' }}
          withElevation
        >
          <InfoRow label='Razón social:' value={basic_data.company_name} />
          <InfoRow label='Referido:' value={referrer_name} />
          <InfoRow label='Oficial:' value={officer} />
          <InfoRow label='CUIT:' value={cuit} />
          <InfoRow
            label='Fecha de constitución:'
            value={basic_data.date_incorporation}
          />
          <InfoRow label='Personería:' value={basic_data.legal_entity} />
          <InfoRow label='Condición frente al IVA:' value={ivaStatus} />
          <InfoRow label='Actividad económica:' value={actividad} fullWidth />
          <Card display='flex'>
            <InfoRow
              label='Domicilio fiscal:'
              value={basic_data.company_address}
              fullWidth
            />
            <Card display='flex' width='580px' gap='16px'>
              <InfoRow label='Localidad:' value={basic_data.state} />
              <InfoRow label='Provincia:' value={basic_data.city} />
            </Card>
          </Card>
          <InfoRow label='Teléfono:' value={basic_data.phone_number} />
          <InfoRow label='Correo electrónico:' value={basic_data.email} />
          <Card
            variant='success'
            modifier='inverted'
            width='100%'
            justifyItems='center'
            border
            margin={{ top: '3' }}
            spacing={'3'}
          >
            {clientData.approved_amount &&
              clientData.approved_amount.map(
                (amount: amount, index: number) => (
                  <Card
                    key={index}
                    display='flex'
                    justifyContent='space-between'
                    width='20%'
                  >
                    <H
                      type='3'
                      weight='bold'
                      modifier='strong'
                      align='center'
                      withMargin={false}
                    >
                      {amount.currency_code}
                      {'  '} {formatCurrency(amount.value)}
                    </H>
                  </Card>
                )
              )}
            <Card margin={{ right: '2', top: '2' }}>
              <H type='4' weight='bold' modifier='strong' withMargin={false}>
                Valor de fianza: ARS {clientData.note_amount}
              </H>
              <H type='4' weight='bold' modifier='strong' withMargin={false}>
                Valor de pagaré: ARS {clientData.guarantee_amount}
              </H>
            </Card>
          </Card>
        </Card>
      </Card>
    );
  };

  const LineRow = ({ line }: { line: Line }) => {
    const isWarningLine = hasActivated && line.instrumentation_id === null;
    const isSuccessLine = line.instrumentation_id !== null;
    const progress = instrumentationStatus.get(line.id) || line.instrumentation_progress;

    return (
      <Card
        display='flex'
        flexDirection='column'
        variant={
          isSuccessLine ? 'success' : isWarningLine ? 'warning' : 'neutral'
        }
        modifier='weak'
        margin={{ bottom: '3' }}
        withElevation
        spacing={'1'}
      >
        <Card
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          width='100%'
        >
          <Card width='35%' margin={{ left: '2' }}>
            <P type='1' weight='bold' modifier='strong' variant='neutral'>
              {productLines &&
                productLines.find(
                  (item: any) => item.code === line.product_line_code
                )?.label}
            </P>
          </Card>
          <Card width='35%' display='flex' gap='4px'>
            <P type='2' weight='bold' variant='neutral'>
              Moneda:
            </P>
            <P type='2' variant='neutral'>
              {line.amount.currency_code}
            </P>
          </Card>
          <Card width='15%' display='flex' gap='4px'>
            <P type='2' weight='bold' variant='neutral'>
              Monto:
            </P>
            <P type='2' variant='neutral'>
              {line.amount.value}
            </P>
          </Card>
          <Card width='15%' display='flex'>
            {isSuccessLine && !progress && (
              <Card display='flex' margin={{ left: '9' }}>
                <Card
                  display='flex'
                  justifyContent='flex-end'
                  margin={{ left: '3' }}
                >
                  <Image name='check_line' />
                </Card>
              </Card>
            )}
            {isWarningLine && !progress && (
              <Card display='flex' margin={{ left: '9' }}>
                <Card
                  display='flex'
                  justifyContent='flex-end'
                  margin={{ left: '3' }}
                >
                  <Image
                    name='warning_line'
                    variant='warning'
                    modifier='strong'
                  />
                </Card>
              </Card>
            )}
          </Card>
        </Card>
        {progress && (
          <Card margin={{ left: '2', top: '2' }}>
            <LineStatusIndicator progress={progress} />
          </Card>
        )}
      </Card>
    );
  };

  const LinesSection = () => (
    <Card variant='none' spacing='2' margin={{ bottom: '4' }}>
      <P variant='neutral' type='1' weight='bold' modifier='strong'>
        Líneas
      </P>
      {lines?.map((line: Line, index: number) => (
        <LineRow key={index} line={line} />
      ))}
    </Card>
  );

  const handleApplication = (status: string, modalText: string) => {
    setClosing(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud de este cliente.'
      );
      mapStatus.set(
        APPLICATION_STATUS.finished.label,
        'Cerraste la carpeta de este cliente.'
      );
      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'instrumentation-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
        setClosing(false);
      } else {
        useToast.error({
          title: 'Ocurrió un error al cerrar la carpeta.',
          id: 'instrumentation-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        setClosing(false);
      }
    });
  };

  return (
    <Card variant='none' spacing='4'>
      <Toast id='instrumentation-toast' />
      {isLoading ? (
        <Card
          height='400px'
          alignItems='center'
          display='flex'
          justifyContent='center'
        >
          <Spinner size='md' variant='primary' />
        </Card>
      ) : (
        <Card>
          <RejectApplicationModal
            isOpen={isRejectModalOpen}
            onClose={() => setRejectModalOpen(false)}
            onSecondaryAction={() => setRejectModalOpen(false)}
            onPrimaryAction={(rejectText: string) => {
              setRejectModalOpen(false);
              handleApplication(APPLICATION_STATUS.rejected.label, rejectText);
            }}
            loadingPrimaryBtn={isClosing}
          />

          <CloseApplicationModal
            isOpen={isCloseModalOpen}
            onClose={() => setCloseModalOpen(false)}
            onSecondaryAction={() => setCloseModalOpen(false)}
            onPrimaryAction={(closeText: string) => {
              setCloseModalOpen(false);
              handleApplication(APPLICATION_STATUS.finished.label, closeText);
            }}
            loadingPrimaryBtn={isClosing}
          />
          <ClientDataBox />
          <LinesSection />
          {lines?.length > 0 && isContractLineActivated && !isAllActivated && (
            <Card
              display='flex'
              justifyContent='flex-end'
              gap='16px'
              margin={{ bottom: '4' }}
            >
              <Card width='220px'>
                {isActivatingLines ? (
                  <Card display='flex' justifyContent='center' alignItems='center' width='100%'>
                    <Spinner size='sm' variant='primary' />
                  </Card>
                ) : (
                  <Button
                    variant='secondary'
                    onClick={handleActivateLines}
                    fullWidth
                  >
                    {hasActivated && hasAnyNullInstrumentation
                      ? 'Reintentar'
                      : 'Activar'}
                  </Button>
                )}
              </Card>
            </Card>
          )}

          <Card display='flex' justifyContent='space-between'>
            <Card width='200px'>
              <Button 
                variant='secondary' 
                onClick={onPreviousClick} 
                fullWidth
              >
                Volver
              </Button>
            </Card>
            <Card display='flex' justifyContent='flex-end' gap='16px'>
              <Card width='200px'>
                <Button
                  variant='secondary'
                  onClick={() => setRejectModalOpen(true)}
                  fullWidth
                >
                  Declinar
                </Button>
              </Card>
              <Card width='200px'>
                <Button
                  variant='primary'
                  onClick={() => setCloseModalOpen(true)}
                  disabled={!isAllActivated}
                  fullWidth
                >
                  Cerrar carpeta
                </Button>
              </Card>
            </Card>
          </Card>
        </Card>
      )}
    </Card>
  );
};

export default ClientLines;
